import initGdalJs from "gdal3.js";
import { fromArrayBuffer, type GeoTIFFImage } from "geotiff";

const { GDAL_VERSION } = import.meta.env;

export interface Gdal {
  open: (file: File) => Promise<GdalDataset>;
  gdalwarp: (input: GdalDataset, options: string[]) => Promise<GdalDataset>;
  getFileBytes: (output: GdalDataset) => Promise<Uint8Array>;
  close: (input: GdalDataset) => void;
  gdal_translate?: (dataset: GdalDataset, options: string[]) => Promise<GdalDataset>;
}

interface GdalDataset {
  datasets: GdalDataset[];
}

/**
 * Initialize GDAL library
 * @returns GDAL instance
 * @throws Error if initialization fails
 */
export const initGdal = async (): Promise<Gdal> => {
  try {
    const gdalInstance = await initGdalJs({
      path: `https://cdn.jsdelivr.net/npm/gdal3.js@${GDAL_VERSION}/dist/package`,
      useWorker: false,
    });
    return gdalInstance as unknown as Gdal;
  } catch (e) {
    throw new Error("GDAL initialization failed", { cause: e });
  }
};

/**
 * Create object URL from GeoTIFF file, converting to Web Mercator if needed
 * @param file Source GeoTIFF file
 * @param gdal GDAL instance
 * @returns Object URL for the GeoTIFF file
 */
export const createUrl = async (file: File, gdal: Gdal): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const tiff = await fromArrayBuffer(arrayBuffer);
  const image = await tiff.getImage();

  let bytes;
  if (await isWGS84Projection(image)) {
    bytes = await reprojectionToWebMercator(gdal, file);
  } else {
    bytes = new Uint8Array(arrayBuffer);
  }
  const blob = new Blob([bytes.slice()], { type: "image/tiff" });
  return URL.createObjectURL(blob);
};

/**
 * Check if the GeoTIFF image uses WGS84 projection
 * @param image GeoTIFFImage instance
 * @returns true if the image uses WGS84 projection, false otherwise
 */
const isWGS84Projection = async (image: GeoTIFFImage): Promise<boolean> => {
  const geoKeys = await image.getGeoKeys();

  if (!geoKeys) return false;

  return (
    geoKeys.GeographicTypeGeoKey === 4326 ||
    (!!geoKeys.GTCitationGeoKey && geoKeys.GTCitationGeoKey.toLowerCase().includes("wgs84"))
  );
};

/**
 * Reprojection GeoTIFF file from WGS84 to Web Mercator (EPSG:3857)
 * @param file Source file to convert
 * @param gdal GDAL instance
 * @returns Converted file as Blob
 */
const reprojectionToWebMercator = async (gdal: Gdal, file: File): Promise<Uint8Array> => {
  const gFile = await gdal.open(file);
  const input = gFile.datasets[0];
  const options = ["-of", "GTiff", "-s_srs", "EPSG:4326", "-t_srs", "EPSG:3857", "-r", "bilinear"];
  const output = await gdal.gdalwarp(input, options);
  const bytes = await gdal.getFileBytes(output);
  gdal.close(input);

  return bytes;
};

/**
 * Perform georeferencing on an image file using GCPs
 * @param gdal GDAL instance
 * @param imageFile Source image file
 * @param gcps Ground control points for georeferencing
 * @returns Processed GeoTIFF blob and URL
 */
export const georeference = async (
  gdal: Gdal,
  imageFile: File,
  gcps: { sourceX: number; sourceY: number; targetX: number; targetY: number }[],
): Promise<{ blob: Blob; url: string }> => {
  if (!gdal || !imageFile || gcps.length < 3) {
    throw new Error("Missing required inputs for georeferencing");
  }

  const gdalFile = await gdal.open(imageFile);
  const input = gdalFile.datasets[0];
  const options = ["-of", "GTiff", "-a_srs", "EPSG:4326"];

  gcps.forEach((gcp) => {
    options.push("-gcp", String(gcp.sourceX), String(gcp.sourceY), String(gcp.targetX), String(gcp.targetY));
  });

  let output;
  if (typeof gdal.gdal_translate === "function") {
    const translateOptions = [...options, "-co", "TILED=YES", "-co", "COMPRESS=LZW", "-co", "BIGTIFF=YES"];
    output = await gdal.gdal_translate(input as GdalDataset, translateOptions);
  } else {
    const warpOptions = [
      "-of",
      "GTiff",
      "-tps",
      "-order",
      "2",
      "-co",
      "TILED=YES",
      "-co",
      "COMPRESS=LZW",
      "-co",
      "BIGTIFF=YES",
      "-overwrite",
      ...options,
    ];
    output = await gdal.gdalwarp(input as GdalDataset, warpOptions);
  }

  const bytes = await gdal.getFileBytes(output);
  gdal.close(input);
  gdal.close(output);

  const blob = new Blob([bytes.slice()], { type: "image/tiff" });
  const file = new File([blob], "georeferenced.tif", { type: "image/tiff" });
  const url = await createUrl(file, gdal);

  return { blob, url };
};

/**
 * Convert Web Mercator coordinates (meters) to WGS84 (longitude/latitude)
 * @param x x-coordinate in meters
 * @param y y-coordinate in meters
 * @returns [longitude, latitude] coordinates
 */
export const metersToLngLat = (x: number, y: number): [number, number] => {
  const R = 6378137; // Earth radius in meters
  const lng = (x / R) * (180 / Math.PI);
  const lat = (Math.PI / 2 - 2 * Math.atan(Math.exp(-y / R))) * (180 / Math.PI);
  return [lng, lat];
};

/**
 * Calculate center coordinates from GeoTIFF bounds
 * @param bounds GeoTIFF bounding box [minX, minY, maxX, maxY] in Web Mercator
 * @returns Object containing center coordinates and lng/lat bounds
 */
export const getCenterCoords = async (
  bounds: number[],
): Promise<{
  centerLng: number;
  centerLat: number;
  lngLatBounds: [number, number, number, number];
}> => {
  const [minLon, minLat] = metersToLngLat(bounds[0], bounds[1]);
  const [maxLon, maxLat] = metersToLngLat(bounds[2], bounds[3]);
  const lngLatBounds: [number, number, number, number] = [minLon, minLat, maxLon, maxLat];

  const centerLng = (minLon + maxLon) / 2;
  const centerLat = (minLat + maxLat) / 2;

  return {
    centerLng,
    centerLat,
    lngLatBounds,
  };
};
